import ApiExtension from './api-extension'
import { toSnakeCase } from '../object-utils'
import objectHash from 'object-hash'
import { delay } from '../async'
import { ApiFetchError, isNetworkErrorStatus } from '.'

export default class Job extends ApiExtension {
  async fetchJobResult(projectIdentifier, operation, params, options = {}) {
    const {
      filename,
      attachments,
      creationRoute = 'project_jobs',
      creationRouteParams = {
        project_id: projectIdentifier,
      },
      creationParamsWrapper = '',
      progressCallback,
      priority,
    } = options
    params = toSnakeCase(params)
    const jobId = await this.getJobId(
      projectIdentifier,
      operation,
      params,
      attachments
    )
    if (progressCallback) {
      progressCallback({ jobId, events: [] })
    }
    const urlParams = {
      project_id: projectIdentifier,
      id: jobId,
    }
    const url = this.createUrl('project_job', urlParams)
    const createExport = () => {
      const form = new FormData()
      form.append(formParamName('id'), jobId)
      form.append(formParamName('operation'), operation)
      form.append(formParamName('params'), JSON.stringify(params))
      if (filename) {
        form.append(formParamName('filename'), filename)
      }
      if (attachments) {
        attachments.forEach((a, i) =>
          form.append(formParamName(`attachment_${i}`), a, a.filename)
        )
      }
      if (priority != null) {
        form.append('priority', priority)
      }
      return this.fetch(
        creationRoute,
        creationRouteParams,
        {
          method: 'POST',
          body: form,
        },
        { format: '' }
      )
    }

    let retryCount = 0
    let lastEvent = null
    const tryFetch = firstTry => {
      let tryUrl = url
      if (lastEvent != null) {
        tryUrl =
          url + (url.indexOf('?') >= 0 ? '&' : '?') + `last_event=${lastEvent}`
      }
      return window.fetch(tryUrl).then(
        function (res) {
          if (res.status === 200) {
            return res.json()
          } else if (res.status === 202) {
            if (progressCallback) {
              return res
                .json()
                .then(response => {
                  const events = response.events || []
                  if (events && events.length > 0) {
                    progressCallback({ jobId, events: events.map(e => e.data) })
                    lastEvent = events[events.length - 1].id
                  }
                })
                .then(() => delay(tryFetch, 1000))
            } else {
              return delay(tryFetch, 1000)
            }
          } else if (res.status === 404 || (res.status === 500 && firstTry)) {
            return createExport().then(tryFetch)
          } else if (res.status === 500) {
            throw new Error(`Job ${operation} failed (job id ${jobId})`)
          } else if (isNetworkErrorStatus(res.status)) {
            const retryPromise = retry()
            if (retryPromise) {
              return retryPromise
            }
          }

          throw new ApiFetchError(`Unexpected HTTP status ${res.status}.`, res)
        },
        function (err) {
          if (err instanceof TypeError) {
            // Network error, retry
            const retryPromise = retry()
            if (retryPromise) {
              return retryPromise
            }
          }
          throw err
        }
      )
    }

    return tryFetch(true)

    function retry() {
      if (++retryCount < 6) {
        const delayValue = retryCount + 1
        return delay(tryFetch, delayValue * delayValue * 1000)
      }
    }

    function formParamName(n) {
      return creationParamsWrapper ? `${creationParamsWrapper}[${n}]` : n
    }
  }

  getJobs(projectIdentifier) {
    return this.apiBase.fetch('project_jobs', {
      project_id: projectIdentifier,
    })
  }

  /**
   * Fetches a job from the backend; by default, waits for the job
   * to finish processing before returning, this can be disabled
   * by passing `false` to the `waitForProcessed` option.
   * @param {string} projectIdentifier
   * @param {string} jobId
   * @param {*} options
   * @returns {Promise<*>} The job object, or `undefined` if the job
   * has not completed processing and `waitForProcessed` is `false`.
   */
  getJob(projectIdentifier, jobId, options = { waitForProcessed: true }) {
    return this.apiBase.fetch(
      'project_job',
      {
        project_id: projectIdentifier,
        id: jobId,
      },
      { retryHttp202: options.waitForProcessed }
    )
  }

  deleteJob(projectIdentifier, jobId) {
    return this.apiBase.fetch(
      'project_job',
      {
        project_id: projectIdentifier,
        id: jobId,
      },
      { method: 'DELETE' }
    )
  }

  async getJobId(projectIdentifier, operation, params, attachments) {
    const attachmentsHashPromises = (attachments || []).map(
      attachment =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.readAsBinaryString(attachment)
          reader.onload = () => resolve(reader.result)
          reader.onerror = err => reject(new Error(err))
        })
    )

    const hashes = await Promise.all(attachmentsHashPromises)
    const attachmentsHash = hashes.reduce(
      (a, hash) => objectHash({ previous: a, current: hash }),
      ''
    )

    return objectHash({
      project_identifier: projectIdentifier,
      operation,
      params,
      attachmentsHash,
    })
  }

  addNotification(projectIdentifier, jobId, type) {
    return this.apiBase.fetch(
      'notification_project_job',
      {
        project_id: projectIdentifier,
        id: jobId,
      },
      { method: 'POST', body: { type } }
    )
  }

  /**
   * Encodes a filename the same way it will be encoded in a job's returned URLs.
   * @param {string} filename
   * @returns {string}
   */
  urlEncodeFilename(filename) {
    // The presigned URLs are URL encoded, and in addition parenthesis are
    // encoded as %28 and %29, which encodeURIComponent does not handle.
    return encodeURIComponent(filename)
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
  }
}
